import { Container } from "@/backend/models/containers/container";
import { Operation } from "@/backend/models/operation";
import { OperationInterface, OperationParams, UpdateOperationReply, UpdateOperationRequest } from "@/types/operationTypes";
import { immer } from "zustand/middleware/immer";
import { DataTypes, ItemReferenceParams } from "@/types/itemReferenceTypes";
import { create } from "zustand";
import { ContainerInterface, ContainerReply, ContainerTypes, CreateContainerRequest, ROOT_TYPES, Status, UpdateContainerRequest } from "@/types/containerTypes";
import { ParameterParams, ParameterSubjectType } from "@/types/parameterTypes";
import { ContainerBuilder } from "@/backend/models/containers/containerBuilder";
import { Step } from "@/backend/models/containers/step";
import { ContainerController } from "../controllers/containerController";
import { useParameterStore } from "./ParameterStore";
import { OperationController } from "../controllers/operationController";
import { BlockController } from "../controllers/blockController";
import { CreateFromBlockRequest } from "@/backend/express/controllers/blockController";
import { TreeController } from "../controllers/treeController";
import { CreateOperationRequest, CreateOperationReply, OrderOperationsRequest } from "@/backend/express/controllers/operationController";
import { PasteRequest } from "@/backend/express/controllers/treeController";
import { ContainerTreeObject, Instruction, TreeAction, TreeState } from "../types/navigationTypes";
import { CompleteRepeatingStepReply, CompleteRepeatingStepRequest, CreateFromFunctionRequest } from "@/backend/express/controllers/containerController";
import { useGlobalStore } from "./GlobalStore";
import { FusaToastType } from "../toasts/types";
import { I18n } from "@/i18n/I18n";
import { CodeReply, CodeWrapper } from "@/types/codeTypes";
import { CodeController } from "../controllers/codeController";
import { CommandingController } from "../controllers/commandingController";
import { CommandingParams } from "@/backend/models/commanding";
import { GraphController } from "../controllers/graphController";
import { GraphParams } from "@/types/operationTypes";
import { CommandingRun, CommandingRunParams } from "@/backend/models/commandingRun";
import { CommandingRunTask } from "@/backend/models/commandingRunTask";
import { CommandingRunTaskData } from "@/types/commandingRunTaskTypes";
import { CommandingRunController } from "../controllers/commandingRunController";
import { embedOnlyLink } from "@/frontend/embed/embedApp";

export interface UpdatedTreeConsumers {
  updatedOperations: OperationInterface[];
  updatedContainers: ContainerInterface[];
}

export interface UpdatedParamsAndRefs {
  updatedParameters: ParameterParams[];
  updatedReferences: ItemReferenceParams[];
}

export interface UpdatedConsumers extends UpdatedParamsAndRefs, UpdatedTreeConsumers {
}

type ContainerLookup = {[key: string]: ContainerTreeObject};

type TreeStore = {
  rootId: string;
  /** What kind of row rootId names: procedure parameters only hang off a procedure. Unset until the page sets its root. */
  rootType?: ROOT_TYPES;
  selectedId: string;
  defaultNodeLoadState: boolean;
  setOperations: (operations: OperationInterface[]) => void;
  operations: Operation[];
  createOperation: (operation: CreateOperationRequest) => Promise<CreateOperationReply>;
  deleteOperation: (operation: Operation) => Promise<void>;
  updateOperationsInStore: (updatedOperations: OperationInterface[]) => void;
  updateOperation(req: UpdateOperationRequest): Promise<UpdateOperationReply>;
  orderOperations(req: OrderOperationsRequest): Promise<void>;
  setOperation: (updatedOperation: OperationParams) => void;
  getStoreOperationsForParent: (parentId: string) => Operation[];
  updateContainerForParent: (parentId: string, containers: ContainerInterface[]) => void;
  updateStoreContainers:  (container: ContainerInterface) => void;
  updateContainer:  (request: UpdateContainerRequest) => Promise<ContainerReply>;
  updateStatus: (container: Container, status: Status) => Promise<void>;
  failStep: (stepId: string) => Promise<void>;
  retryStep: (stepId: string) => Promise<void>;
  fetchContainer: (containerId: string) => Promise<ContainerReply>;
  duplicateContainer: (id: string) => Promise<void>;
  duplicateOperation: (id: string) => Promise<void>;
  containers: Container[];
  childContainers: (parentId: string, isScaffold?: boolean) => ContainerTreeObject[];
  setContainers: (containers: ContainerInterface[]) => void;
  reorderContainers: (targetContainerId: string, movingContainerId: string, action: Instruction) => Promise<void>;
  createContainer: (request: CreateContainerRequest) => Promise<ContainerReply>;
  deleteContainer: (container: Container) => Promise<void>;
  orderContainers: (containers: ContainerInterface[]) => Promise<void>;
  updateCommanding: (updatedCommanding: CommandingParams, operation: Operation) => Promise<void>;
  commandingRuns: CommandingRun[];
  setCommandingRuns: (commandingRuns: CommandingRunParams[], tasks: CommandingRunTaskData[]) => void;
  upsertCommandingRun: (commandingRun: CommandingRunParams, tasks: CommandingRunTaskData[]) => void;
  upsertRunTask: (task: CommandingRunTaskData) => void;
  fetchCommandingRunsByProcedure: (procedureId: string) => Promise<void>;
  reset: () => void;
  generateFromTemplate: (procedureId: string, functionalInterfaceImplementationAssignments: { [key: string]: string }) => Promise<void>;
  runTasks: CommandingRunTask[];
  generateContainerTree: () => void;
  rootContainerNodes: ContainerTreeObject[];
  templateRootContainerNodes: ContainerTreeObject[];
  containerLookup: ContainerLookup;
  setRootNodes: (rootNodes: ContainerTreeObject[]) => void;
  setTemplateRootNodes: (rootNodes: ContainerTreeObject[]) => void;
  setContainerLookup: (containerLookup: ContainerLookup) => void;
  lastAction?: TreeAction;
  getTreeState: (templateMode: boolean) => TreeState;
  observer?: IntersectionObserver;
  setObserver: (observer: IntersectionObserver) => void;
  loadNode: (containerId: string) => void;
  overrideStepStart: (stepId: string) => void;
  pasteItem: (request: PasteRequest) => Promise<void>;
  createFromBlock(req: CreateFromBlockRequest): Promise<void>;
  fetchRootItemChildren: (rootId: string) => Promise<void>;
  refetchChildren: (parentId: string) => Promise<void>;
  setSelectedId: (selectedId: string) => void;
  updateCode: (request: CodeWrapper, operation: Operation) => Promise<CodeReply>;
  updateGraph: (graph: GraphParams) => Promise<void>;
  handleUpdatedConsumers: (updatedConsumers: UpdatedConsumers) => void;
  handleUpdatedTreeConsumers: (updatedConsumers: UpdatedTreeConsumers) => void;
  handleContainerResponse: (updatedContainers: ContainerInterface[]) => void;
  procedureLoaded: boolean;
  idForScroll: string;
  setIdForScroll: (id: string) => void;
  idForScrollFound: boolean;
  setIdForScrollFound: (found: boolean) => void;
  completeRepeatingStep: (id: string, request: CompleteRepeatingStepRequest) => Promise<CompleteRepeatingStepReply>;
  createContainerFromFunction: (req: CreateFromFunctionRequest) => Promise<void>;
};

const initialState = {
  rootId: '',
  rootType: undefined as ROOT_TYPES | undefined,
  selectedId: '',
  operations: [] as Operation[],
  commandingRuns: [] as CommandingRun[],
  containers: [] as Container[],
  runTasks: [] as CommandingRunTask[],
  rootContainerNodes: [] as ContainerTreeObject[],
  templateRootContainerNodes: [] as ContainerTreeObject[],
  // requiredInstrumentNames: [] as string[],
  containerLookup: {} as ContainerLookup,
  defaultNodeLoadState: false,
  procedureLoaded: false,
  // ipc: new Ipc(),
  idForScroll: '',
  idForScrollFound: false,
};

export const useTreeStore = create<TreeStore>()(immer((set, get) => ({
  ...initialState,
  setIdForScroll: (idForScroll: string) => set({ idForScroll }),
  setIdForScrollFound: (idForScrollFound: boolean) => set({ idForScrollFound }),
  reset: () => {
    set(initialState);
  },
  setRootNodes: (rootNodes) => {
    set({ rootContainerNodes: rootNodes });
  },
  setTemplateRootNodes: (rootNodes) => {
    set({ templateRootContainerNodes: rootNodes });
  },
  setContainerLookup: (containerLookup) => {
    set({ containerLookup });
  },
  generateContainerTree: () => {
    const trees = getInitialContainerData();
    get().setRootNodes(trees.rootContainers);
    get().setTemplateRootNodes(trees.templateContainers);
    get().setContainerLookup(trees.containerLookup);
  },
  getTreeState: (templateMode: boolean) => {
    const { rootContainerNodes, templateRootContainerNodes, lastAction } = get();
    if (templateMode) {
      return { data: templateRootContainerNodes, lastAction };
    }
    return { data: rootContainerNodes, lastAction };
  },
  setOperations: (operations) => {
    const builtOperations = operations.map(operation => new Operation(operation));
    set({ operations: builtOperations  });
  },
  setCommandingRuns: (commandingRuns, tasks) => {
    set({
      commandingRuns: commandingRuns.map(run => new CommandingRun(run)),
      runTasks: tasks.map(task => new CommandingRunTask(task)),
    });
  },
  // A run replaces whatever the commanding had before it, tasks included:
  // `prepare` deletes the commanding's previous runs, so keeping the old rows
  // would leave tasks pointing at a run that no longer exists.
  upsertCommandingRun: (commandingRun, tasks) => {
    const built = new CommandingRun(commandingRun);
    const runs = get().commandingRuns.filter(run => run.commandingId !== built.commandingId);
    // Both the run being replaced and any previous run of the same commanding
    // take their tasks with them; a task left behind points at a run the store
    // no longer holds, and nothing would ever clear it.
    const replacedIds = new Set([built.id, ...get().commandingRuns
      .filter(run => run.commandingId === built.commandingId)
      .map(run => run.id)]);
    const otherTasks = get().runTasks.filter(task => !replacedIds.has(task.commandingRunId));
    set({
      commandingRuns: [...runs, built],
      runTasks: [...otherTasks, ...tasks.map(task => new CommandingRunTask(task))],
    });
  },
  /**
   * Replaces one run task in place, for the file a confirmed upload attaches.
   *
   * That happens after the row was last read, so the row the store is holding
   * has no asset on it; replacing the whole run instead would discard
   * everything else the same message brought.
   *
   * A task the store does not hold is ignored rather than added. It belongs to
   * a run that has since been replaced, and putting it back would leave a task
   * pointing at a run that is gone.
   */
  upsertRunTask: (task) => {
    const built = new CommandingRunTask(task);
    if (!get().runTasks.some(existing => existing.id === built.id)) return;

    set({ runTasks: get().runTasks.map(existing => (existing.id === built.id ? built : existing)) });
  },
  fetchCommandingRunsByProcedure: async (procedureId) => {
    const response = await new CommandingRunController().readAllForProcedure(procedureId);
    if (response.status !== 200) return;
    get().setCommandingRuns(response.commandingRuns, response.tasks);
  },
  createOperation: async (operationReq: CreateOperationRequest) => {
    const response = await new OperationController().createOperation(operationReq);
    if (response.errors && response.errors.length > 0) {
      return response;
    }
    if (response.parameters) {
      const { parameters, setParameters } = useParameterStore.getState();
      setParameters([...parameters, ...response.parameters]);
    }
    const updatedListResponse = await new OperationController().listOperationsByParentId(response.operation.parentId);
    if (updatedListResponse.status !== 200) return response;
    const { operations, setOperations } = get();
    const operationsToNotReplace = operations.filter((o) => o.parentId !== response.operation.parentId);
    setOperations([...operationsToNotReplace, ...updatedListResponse.operations]);
    return response;
  },
  deleteOperation: async (operation: Operation): Promise<void> => {
    const response = await new OperationController().deleteOperation(operation.id);
    if (response.status !== 200) return;

    const updatedListResponse = await new OperationController().listOperationsByParentId(operation.parentId);

    const { operations, setOperations } = get();
    const { references, setReferences, parameters, setParameters } = useParameterStore.getState();
    const operationsToNotReplace = operations.filter((op) => op.parentId !== operation.parentId);
    const operationParameter = parameters.find((param) => param.subjectId === operation.id);
    setParameters(parameters.filter((param) => param.subjectId !== operation.id));
    setOperations([...operationsToNotReplace, ...updatedListResponse.operations]);
    setReferences(references.filter(ref =>
      !(ref.dataConsumerId === operation.id && ref.dataConsumerType === DataTypes.Operation)
      && !(ref.dataSourceId === operationParameter?.id && ref.dataSourceType === DataTypes.Parameter)
    ));
  },
  getStoreOperationsForParent: (parentId: string) => {
    return get().operations
    .filter((operation) => operation.parentId === parentId)
    .sort((a, b) => a.position - b.position);
  },

  handleUpdatedConsumers: (updatedConsumers) => {
    const { updatedOperations, updatedContainers } = updatedConsumers;
    const { handleUpdatedParamsAndRefs } = useParameterStore.getState();
    handleUpdatedParamsAndRefs(updatedConsumers);
    get().handleUpdatedTreeConsumers({ updatedOperations, updatedContainers: updatedContainers || [] });
  },
  handleUpdatedTreeConsumers: (updatedConsumers) => {
    const { updatedContainers, updatedOperations } = updatedConsumers;
    get().handleContainerResponse(updatedContainers);
    get().updateOperationsInStore(updatedOperations);
  },
  updateOperationsInStore: (updatedOperations) => {
    if (updatedOperations.length === 0) return;
    const updatedOperationLookup: { [key: string]: Operation } = updatedOperations.reduce((acc, operation) => ({ ...acc, [operation.id]: operation }), {});
    const { operations, setOperations } = get();
    setOperations(operations.map(operation => updatedOperationLookup[operation.id] || operation));
  },
  updateOperation: async (req: UpdateOperationRequest): Promise<UpdateOperationReply> => {
    const response = await new OperationController().updateOperation(req);
    if (!response.errors && response.updatedOperations.length > 0) {
      get().handleUpdatedConsumers({ ...response, updatedContainers: [] });
    }
    return response;
  },
  orderOperations: async (req: OrderOperationsRequest) => {
    const response = await new OperationController().orderOperations(req);
    if (response.status !== 200) return;
    get().updateOperationsInStore(response.operations);
  },
  setOperation: (updatedOperation) => {
    const { operations, setOperations } = get();
    setOperations(operations.map(o => o.id === updatedOperation.id ? new Operation(updatedOperation) : o));
  },
  updateContainerForParent: (parentId, incomingContainers) => {
    get().setContainers([...get().containers.filter((s) => s.parentId !== parentId), ...incomingContainers]);
  },
  reorderContainers: async (targetContainerId: string, movingContainerId: string, instruction: Instruction) => {
    const response = await new ContainerController().reorder({ targetContainerId, movingContainerId, instruction });
    if (response.status !== 200) return;

    const movingUpdated = response.targetContainers.find((container) => container.id === movingContainerId);
    if (movingUpdated) {
      get().updateStoreContainers(movingUpdated);
    }

    if (response.targetContainers.length > 0) {
      get().updateContainerForParent(response.targetContainers[0].parentId, response.targetContainers);
    }
    if (response.movingContainerSiblings.length > 0 && response.movingContainerSiblings[0].parentId !== response.targetContainers[0].parentId) {
      get().updateContainerForParent(response.movingContainerSiblings[0].parentId, response.movingContainerSiblings);
    }
  },
  setContainers: (containers) => {
    const builtContainers = containers.map((container) => {
      return new ContainerBuilder().build(container);
    }).sort((a, b) => a.position - b.position);
    set({ containers: builtContainers });
    get().generateContainerTree();
},
  childContainers: (parentId, isScaffold) => {
    const parentContainer = get().containerLookup[parentId];
    if (parentContainer) return parentContainer.children;
    const children = isScaffold ? get().templateRootContainerNodes : get().rootContainerNodes;
    return children;
  },
  createContainer: async (request: CreateContainerRequest) => {
    const response = await new ContainerController().create(request);
    if (response.status === 201 && response.container) {
      await get().refetchChildren(response.container.parentId);
      get().loadNode(response.container.id);
    }
    return response;
  },
  deleteContainer: async (container) => {
    return new ContainerController().delete(container.id).then(() => {
      const containerIdsToRemove = gatherSubtreeIds(container.id);
      const { containers, setContainers, operations, setOperations } = get();
      const { parameters, setParameters, references, setReferences } = useParameterStore.getState();
      const updatedContainers = containers.filter((c) => !containerIdsToRemove.includes(c.id));
      setContainers(updatedContainers);
      const updatedOperations = operations.filter((o) => !containerIdsToRemove.includes(o.parentId));
      setOperations(updatedOperations);

      const operationIds = updatedOperations.map(({ id }) => id);
      const containerIds = updatedContainers.map(({ id }) => id);
      const updatedParameters = parameters.filter(param =>
        (param.subjectType === ParameterSubjectType.STEP && containerIds.includes(param.subjectId))
        || (param.subjectType === ParameterSubjectType.OPERATION && operationIds.includes(param.subjectId)
        || param.subjectType === ParameterSubjectType.PROCEDURE
      )
      );
      setParameters(updatedParameters);

      const parameterIds = updatedParameters.map(({ id }) => id);
      const updatedReferences = references.filter(ref => {
        if (ref.dataSourceType === DataTypes.Operation && !operationIds.includes(ref.dataSourceId)) return false;
        if (ref.dataConsumerType === DataTypes.Operation && !operationIds.includes(ref.dataConsumerId)) return false;
        if (ref.dataSourceType === DataTypes.Parameter && !parameterIds.includes(ref.dataSourceId)) return false;
        if (ref.dataConsumerType === DataTypes.Parameter && !parameterIds.includes(ref.dataConsumerId)) return false;
        return true;
      });
      setReferences(updatedReferences);

      void get().refetchChildren(container.parentId);
    });
  },
  orderContainers: async (containers: ContainerInterface[]) => {
    const response = await new ContainerController().order({ containers });
    if (response.status !== 200) return;
    get().updateContainerForParent(response.containers[0].parentId, response.containers);
  },
  updateContainer: async (request) => {
    const response = await new ContainerController().update(request);
    if (response.status === 200) get().updateStoreContainers(response.container as Container);
    return response;
  },
  updateStatus: async (container: Container, status: Status) => {
    await get().updateContainer({ container: { ...container, status } });
    if (container.isRepeatingStepChild() && status === Status.PENDING) {
      const parent = get().containerLookup[container.parentId];
      if (parent) {
        await get().updateContainer({ container: { ...parent.container, status } });
      }
    }
  },
  failStep: async (stepId: string) => {
    const step = get().containerLookup[stepId];
    if (!step) return;
    await get().updateStatus(step.container, Status.FAILED);
  },
  retryStep: async (stepId: string) => {
    const response = await new ContainerController().resetStep(stepId);
    if (response.status === 200) {
      await get().refetchChildren(stepId);
      //await get().fetchCommandingRunsByProcedure()
      // Cleared values make their validations stale, and this answers with no
      // parameters to carry them, so they are run from here rather than
      // through `handleUpdatedParamsAndRefs`.
      void useParameterStore.getState().runPendingValidations(response.pendingValidations ?? []);
    }
  },
  fetchContainer: async (containerId: string) => {
    const response = await new ContainerController().read(containerId);
    if (response.status === 200 && response.container) {
      get().updateStoreContainers(response.container);
    }
    return response;
  },
  updateStoreContainers(container) {
    const containers = get().containers.map((storeContainer) => (container.id === storeContainer.id ? container : storeContainer));
    get().setContainers(containers);
  },
  duplicateContainer: async (id) => {
    const response = await new ContainerController().duplicate(id);
    if(response.status !== 200 || !response.container) return;
    void get().refetchChildren(response.container.parentId);
  },
  createFromBlock: async (req) => {
    const response = await new BlockController().createFromBlock(req);
    if (response.status === 200) {
      void get().refetchChildren(req.targetParentId);
    }
  },
  duplicateOperation: async (id: string) => {
    const response = await new OperationController().duplicateOperation(id);
    if(response.status !== 200) return;
    void get().refetchChildren(response.operation.parentId);
  },
  updateCommanding: async (updatedCommanding: CommandingParams, operation: Operation) => {
    const response = await new CommandingController().updateCommanding(updatedCommanding);
    if (response.status === 200 && response.commanding) {
      get().setOperation(new Operation({ ...operation, commanding: response.commanding }));
      const { handleReferenceResponse } = useParameterStore.getState();
      handleReferenceResponse(response.updatedReferences);
    }
  },
  generateFromTemplate: async (procedureId, functionalInterfaceImplementationAssignments) => {
    const response = await new ContainerController().generateContainers(procedureId, functionalInterfaceImplementationAssignments);
    if (response.status === 200) {
      void get().fetchRootItemChildren(procedureId);
      // A generated procedure's validations have never run, so the run view
      // would open showing every one of them as failing.
      void useParameterStore.getState().runPendingValidations(response.pendingValidations ?? []);
    } else if (response.templateError) {
      useGlobalStore.getState().addToast({
        message: response.templateError.message,
        link: embedOnlyLink(`/procedures/scaffold/${procedureId}#${response.templateError.itemId}`),
        linkText: response.templateError.additionalDetail,    
        header: I18n.get('error'),
        type: FusaToastType.ERROR,
      });
    }
  },
  setObserver: (observer) => set({ observer }),
  loadNode: (containerId: string) => {
    const {containerLookup} = get();
    const myNode = containerLookup[containerId];
    if (!myNode) {
      return;
    }
    if (myNode.loaded) return;
    myNode.loaded = true;
    containerLookup[containerId] = { ...myNode};

    set({containerLookup: {...containerLookup}});
  },
  overrideStepStart: (stepId: string) => {
    const { containerLookup } = get();
    const step = containerLookup[stepId];
    if (!step) return;
    if (step.stepStartOverridden) return;

    containerLookup[stepId] = { ...step, stepStartOverridden: true };

    set({ containerLookup: { ...containerLookup }});
  },
  pasteItem: async (request: PasteRequest) => {
    const response = await new TreeController().pasteItem(request);
    if (response.errors || response.status !== 200) return;
    if (response.newItem?.parentId) {
      void get().refetchChildren(response.newItem.parentId);
    }
  },
  fetchRootItemChildren: async (rootId: string) => {
    const response = await new TreeController().getChildren(rootId);
    if (response.errors) return;
    const { setContainers, setOperations } = get();
    const { setParameters, setReferences, setValidations } = useParameterStore.getState();
    setContainers(response.containers);
    setOperations(response.operations);
    setValidations(response.validations);
    setParameters(response.parameters);
    setReferences(response.references);
  },
  refetchChildren: async (parentId) => {
    const response = await new TreeController().getChildren(parentId);
    if (response.status !== 200) return;

    const {
      operations,
      setOperations,
      containers,
      setContainers,
    } = get();

    if (response.operations.length > 0) {
      const responseIds = response.operations.map(cont => cont.id);
      const filteredOperations = operations.filter(op => parentId !== op.parentId && !responseIds.includes(op.id));
      setOperations([...filteredOperations, ...response.operations]);
    }
    if (response.containers.length > 0) {
      const responseIds = response.containers.map(cont => cont.id);
      const filteredContainers = containers.filter(cont => parentId !== cont.parentId && !responseIds.includes(cont.id));
      setContainers([...filteredContainers, ...response.containers]);
    }

    const { handleUpdatedParamsAndRefs, handleValidationResponse } = useParameterStore.getState();
    handleUpdatedParamsAndRefs({ updatedParameters: response.parameters, updatedReferences: response.references });
    handleValidationResponse(response.validations);
  },
  handleContainerResponse: (updatedContainers) => {
    const { containers, setContainers } = get();
    if (updatedContainers.length > 0) {
      const responseIds = updatedContainers.map(ref => ref.id);
      const filteredContainers = containers.filter(container => !responseIds.includes(container.id));
      setContainers([...filteredContainers, ...updatedContainers]);
    }
  },
  setSelectedId: (newSelection) => {
    const { selectedId, containerLookup, rootId } = get();
    setSelected(selectedId, false, containerLookup, rootId);
    setSelected(newSelection, true, containerLookup, rootId);
    set({ containerLookup: { ...containerLookup }, selectedId: newSelection });
  },
  updateCode: async (request: CodeWrapper, operation: Operation) => {
    const controller = new CodeController();
    const response = await controller.updateCode(request);
    if (!response.errors) {
      get().setOperation(new Operation({ ...operation, code: response.code }));
    }
    return response;
  },
  updateGraph: async (graph: GraphParams) => {
    const controller = new GraphController();
    const response = await controller.updateGraph({ graph });
    if (response.status === 200 && response.operation) {
      get().setOperation(new Operation(response.operation));
    }
  },
  completeRepeatingStep: async (repeatingStepId, request) => {
    const response = await new ContainerController().completeRepeatingStep(repeatingStepId, request);
    if (response.status === 200) {
      if (response.nextStep) {
        await get().refetchChildren(repeatingStepId);
      }
      const { handleParameterResponse } = useParameterStore.getState();
      handleParameterResponse(response.updatedParameters);
      get().updateStoreContainers(response.repeatingStep);
    }
    return response;
  },
  createContainerFromFunction: async (req: CreateFromFunctionRequest) => {
    const response = await new ContainerController().createFromFunction(req);
    if (response.status === 200) {
      await get().refetchChildren(req.parentId);
    }
  },
})));

function gatherSubtreeIds(parentId: string): string[] {
  const { children } = useTreeStore.getState().containerLookup[parentId];
  const childIds = children.map(({ id }) =>  [...gatherSubtreeIds(id), id]);
  return [...childIds.flat(), parentId];
}

type ContainerTrees = {
  rootContainers: ContainerTreeObject[];
  templateContainers: ContainerTreeObject[];
  containerLookup: {[key: string]: ContainerTreeObject};
};

export function getInitialContainerData(): ContainerTrees {
  const { containers, containerLookup: oldContainerLookup, rootId, defaultNodeLoadState: defaultNodeState } = useTreeStore.getState();

  const rootContainers: ContainerTreeObject[] = [];
  const templateContainers: ContainerTreeObject[] = [];
  const containerLookup: ContainerLookup = {};
  containers.forEach((container: Container) => {
    const oldContainer = oldContainerLookup[container.id];
    containerLookup[container.id] = {
      container,
      id: container.id,
      parentId: container.parentId,
      children: [],
      isOpen: true,
      loaded: oldContainer ? oldContainer.loaded : defaultNodeState,
      selected: oldContainer?.selected || false,
      shouldRender: container.containerType !== ContainerTypes.Scenario && container.containerType !== ContainerTypes.RepeatingStepChild,
      stepStartOverridden: oldContainer?.stepStartOverridden || false,
    };
  });

  containers.forEach((container) => {
    const currentContainerNode = containerLookup[container.id] as ContainerTreeObject;
    if (!currentContainerNode) {
      return;
    };
    const ultimateParent = rootId || currentContainerNode.container.rootId;
    if (currentContainerNode.parentId === ultimateParent) {
      if (!currentContainerNode.container.isScaffold) {
        rootContainers.push(currentContainerNode);
      } else {
        templateContainers.push(currentContainerNode);
      }
    } else if (currentContainerNode.parentId in containerLookup) {
      const parentContainer = containerLookup[currentContainerNode.parentId];
      parentContainer.children.push(currentContainerNode);
    }
  });

  new PositionSetter().setWBS(rootContainers, '');
  new PositionSetter().setWBS(templateContainers, '');

  return { rootContainers, templateContainers, containerLookup };
}

class PositionSetter {
  stepPosition: number;

  constructor() {
    this.stepPosition = 0;
  }

  setWBS(nodes: ContainerTreeObject[], parentWBS: string) {
    let wbsIndex = 1;
    nodes.forEach(node => {
      if (node.container.containerType === ContainerTypes.Section) {
        const wbs = `${parentWBS}${wbsIndex}`;
        wbsIndex += 1;
        node.wbs = wbs;
        this.setWBS(node.children, `${wbs}.`);
      }
      if (node.container instanceof Step) {
        node.stepPosition = this.stepPosition;
        this.stepPosition += 1;
      }
    });
  }
}

function setSelected(id: string, selected: boolean, containerLookup: ContainerLookup, rootId: string) {
  const node = containerLookup[id];
  if (node) {
    containerLookup[id] = { ...node, selected };

    if (node.parentId !== rootId) {
      setSelected(node.parentId, selected, containerLookup, rootId);
    }
  }
}
